// @ts-nocheck
import { forwardRef, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  ThemeIcon,
  useMantineTheme,
  useComputedColorScheme,
} from '@mantine/core';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { Paper } from '../../../ui';
import { FadeIn, HoverLift } from '../../../animations';
import { pickTextColorForBackground } from '../../../../utils/color';
import type { KpiCardProps } from '../types';

/**
 * KpiCardWithSparkline - KPI card with mini trend chart
 *
 * Displays a KPI with a small line chart showing historical trend.
 * Perfect for showing performance over time at a glance.
 */
const KpiCardWithSparkline = forwardRef<HTMLDivElement, KpiCardProps>(
  (
    {
      title,
      value,
      description,
      icon,
      color = 'blue',
      size = 'md',
      variant = 'default',
      gradientVariant = 'primary',
      sparklineData = [],
      onClick,
      animate = false,
      animationDuration = 2000,
      className,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme('light');
    const gradient = theme.other?.gradients?.[gradientVariant];

    const [displayValue, setDisplayValue] = useState(animate ? 0 : value);

    const sizes = {
      xs: {
        padding: 'xs',
        iconSize: 28,
        titleSize: '10px',
        valueSize: 'lg',
        chartHeight: 30,
        gap: 'xs',
      },
      sm: {
        padding: 'sm',
        iconSize: 32,
        titleSize: 'xs',
        valueSize: 'xl',
        chartHeight: 40,
        gap: 'sm',
      },
      md: {
        padding: 'md',
        iconSize: 40,
        titleSize: 'sm',
        valueSize: '2rem',
        chartHeight: 50,
        gap: 'md',
      },
      lg: {
        padding: 'lg',
        iconSize: 48,
        titleSize: 'md',
        valueSize: '2.5rem',
        chartHeight: 60,
        gap: 'lg',
      },
      xl: {
        padding: 'xl',
        iconSize: 56,
        titleSize: 'lg',
        valueSize: '3rem',
        chartHeight: 70,
        gap: 'xl',
      },
    };

    const currentSize = sizes[size];

    // Animated counter
    useEffect(() => {
      if (!animate || typeof value !== 'number') {
        setDisplayValue(value);
        return;
      }

      const startValue = 0;
      const endValue = value;
      const duration = animationDuration;
      const startTime = Date.now();

      const animateValue = () => {
        const now = Date.now();
        const progress = Math.min((now - startTime) / duration, 1);
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const currentValue = Math.floor(startValue + (endValue - startValue) * easeOut);

        setDisplayValue(currentValue);

        if (progress < 1) {
          requestAnimationFrame(animateValue);
        } else {
          setDisplayValue(endValue);
        }
      };

      requestAnimationFrame(animateValue);
    }, [value, animate, animationDuration]);

    // Prepare sparkline data
    const chartData = sparklineData.map((val, idx) => ({ value: val, index: idx }));

    // Determine line color based on trend
    const getSparklineColor = () => {
      if (sparklineData.length < 2) return theme.colors[color][6];

      const firstValue = sparklineData[0];
      const lastValue = sparklineData[sparklineData.length - 1];

      if (lastValue > firstValue) {
        return theme.colors.teal[6];
      } else if (lastValue < firstValue) {
        return theme.colors.red[6];
      }
      return theme.colors.gray[6];
    };

    const sparklineColor = getSparklineColor();

    const paperVariant =
      variant === 'gradient' ? 'gradient' : variant === 'outlined' ? 'outlined' : 'default';

    const isGradient = variant === 'gradient';

    // Calculate dynamic text color for gradient backgrounds
    const textColor = useMemo(() => {
      if (!isGradient || !gradient) return undefined;
      const gradientString = `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`;
      return pickTextColorForBackground(gradientString);
    }, [isGradient, gradient]);

    return (
      <FadeIn>
        <HoverLift disabled={!onClick}>
          <Paper
            ref={ref}
            variant={paperVariant}
            gradientVariant={gradientVariant}
            p={currentSize.padding}
            onClick={onClick}
            className={className}
            style={{
              cursor: onClick ? 'pointer' : 'default',
              height: '100%',
            }}
          >
            <Stack gap={currentSize.gap}>
              {/* Header with icon */}
              <Group justify="space-between" align="flex-start">
                <Text
                  size={currentSize.titleSize}
                  c={isGradient ? textColor : 'dimmed'}
                  tt="uppercase"
                  fw={700}
                  style={{ letterSpacing: '0.5px' }}
                >
                  {title}
                </Text>

                {icon && (
                  <ThemeIcon
                    size={currentSize.iconSize}
                    radius="md"
                    variant={isGradient ? 'transparent' : 'light'}
                    color={isGradient ? undefined : color}
                    style={{
                      background: isGradient ? 'rgba(255,255,255,0.2)' : undefined,
                      color: isGradient ? textColor : undefined,
                    }}
                  >
                    {icon}
                  </ThemeIcon>
                )}
              </Group>

              {/* Value */}
              <Text
                size={currentSize.valueSize}
                fw={700}
                c={isGradient ? textColor : undefined}
                style={{
                  lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {displayValue}
              </Text>

              {/* Sparkline chart */}
              {sparklineData.length > 0 && (
                <Box style={{ width: '100%', height: currentSize.chartHeight }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke={isGradient ? textColor : sparklineColor}
                        strokeWidth={2}
                        dot={false}
                        animationDuration={1000}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}

              {/* Description */}
              {description && (
                <Text
                  size="xs"
                  c={isGradient ? textColor : 'dimmed'}
                  style={{ opacity: isGradient ? 0.9 : 1 }}
                >
                  {description}
                </Text>
              )}
            </Stack>
          </Paper>
        </HoverLift>
      </FadeIn>
    );
  }
);

KpiCardWithSparkline.displayName = 'KpiCardWithSparkline';

export default KpiCardWithSparkline;
